import { ApiRunLogSchema, type ApiRunLog } from "@/lib/api/schemas/apiRunLog";
import { CycleStateSchema, StepsSchema } from "@/lib/api/schemas/cycle";
import { JiraIssueBadge } from "@/lib/api/schemas/jira";
import { ProjectType } from "@/lib/domain/platform";
import { NextgenStepsSchema, ProcessingTypeSchema } from "@/lib/api/schemas/cycleNextgen";
import { z } from "zod";
import { DateTimeWithOffset, NullishDateTimeWithOffset } from "@/lib/api/schemas/patterns";
import { Auth } from "../auth";
import { request, validateInput } from "../request";
import type { ActionReturnType } from "../types";
import { UsageStatisticsSchema } from "@/lib/api/schemas/shared";

export type CyclesListSlimResponse = z.infer<typeof CyclesEndpoint.schemas.listSlim.response>;
export type CycleReview = z.infer<typeof CyclesEndpoint.schemas.review.response>;
export type CycleReviewVerdict = "expected" | "defect" | "flaky";

const ReviewDecisionSchema = z.object({
  /** `expected`, `defect` or `flaky`; empty while nobody has ruled. */
  verdict: z.string(),
  comment: z.string(),
});

class CyclesEndpoint {
  public static readonly basePath = "cycles";
  public static readonly schemas = {
    /**
     * `cycles/list/` — not `cycles/?case_id=`, which carries every step of every cycle with the
     * worker's log in it (~730 KB a row) and which nothing here reads any more.
     *
     * Only the fields the app reads are declared; the contract-drift test reports the rest
     * as INFO rather than letting them rot here.
     */
    listSlim: {
      parameters: z.object({
        project_ids: z.number().array().optional(),
        worker_ids: z.number().array().optional(),
        schedule_ids: z.number().array().optional(),
        /** Filters by the exact case version, not its lineage. */
        test_case_ids: z.number().array().optional(),
        test_suite_ids: z.number().array().optional(),
        platform: ProjectType.array().optional(),
        states: CycleStateSchema.array().optional(),
        created_at_from: z.union([z.string(), z.date()]).optional(),
        created_at_to: z.union([z.string(), z.date()]).optional(),
        is_playground: z.boolean().optional(),
        include_preconditions: z.boolean().optional(),
        ordering: z.string().optional(),
        limit: z.number().int().positive().optional(),
        offset: z.number().int().min(0).optional(),
      }),
      response: z.object({
        count: z.number(),
        items: z
          .object({
            id: z.number(),
            created_at: DateTimeWithOffset,
            started_processing_at: NullishDateTimeWithOffset,
            finished_processing_at: NullishDateTimeWithOffset,
            state: CycleStateSchema,
            // `nullish` rather than `nullable`: the contract marks these optional,
            // and a slimmed-down list that drops a key is exactly how the schedules
            // list broke the app once already.
            case_id: z.number().nullish(),
            case_name: z.string().nullish(),
            schedule_id: z.number().nullish(),
            project_id: z.number().nullish(),
            project_name: z.string().nullish(),
            platform: z.string().nullish(),
          })
          .array(),
      }),
    },
    read: {
      response: z.preprocess(
        // Duplicate the raw `steps` into `nextgenSteps` so the same payload is parsed both
        // with the classic shape (StepsSchema) and the next-gen shape (NextgenStepsSchema).
        (raw) =>
          raw && typeof raw === "object" && !Array.isArray(raw)
            ? { ...(raw as Record<string, unknown>), nextgenSteps: (raw as { steps?: unknown }).steps ?? null }
            : raw,
        z
          .object({
            id: z.number(),
            state: CycleStateSchema,
            created_at: DateTimeWithOffset,
            started_processing_at: NullishDateTimeWithOffset,
            finished_processing_at: NullishDateTimeWithOffset,
            stream_messages: z.boolean(),
            processing_type: ProcessingTypeSchema,
            removed_payload_at: NullishDateTimeWithOffset,
            worker_id: z.number().nullable(),
            case_id: z.number().nullable(),
            schedule_id: z.number().nullable(),
            precondition_for_cycle_id: z.number().nullable(),
            precondition_for_cycle_name: z.string().nullable(),
            precondition_cycle_id: z.number().nullable(),
            precondition_cycle_name: z.string().nullable(),
            steps: StepsSchema.nullable(),
            nextgenSteps: NextgenStepsSchema.nullable(),
            manually_overridden: z.boolean(),
            jira_issues: z.array(JiraIssueBadge).default([]),
          })
          .and(UsageStatisticsSchema),
      ),
    },
    /**
     * `cycles/{id}/review` — the one way to change a finished cycle's state; `PUT cycles/{id}/`
     * takes timestamps only. A verdict is not a free "set state": `expected` turns an anomaly
     * run into `success`, `defect` turns a success or an anomaly run into `failure`, `flaky`
     * turns any run into `warning`, `revoke` restores what the run said. `expected` and `defect`
     * also reach the agent on the next run of the case; `flaky` does not.
     *
     * `state` already carries the override; `state_before_override` is what the run reported.
     */
    review: {
      body: z.object({
        anomaly_ids: z.number().array().optional(),
        comment: z.string().optional(),
      }),
      response: z.object({
        cycle_id: z.number(),
        state: CycleStateSchema,
        manually_overridden: z.boolean(),
        /** Null while the run speaks for itself. */
        state_before_override: CycleStateSchema.nullable(),
        overridden_at: NullishDateTimeWithOffset,
        overridden_by: z.string(),
        /** A run that flagged nothing is ruled on as a whole; `verdict` is empty otherwise. */
        run_verdict: ReviewDecisionSchema,
        /** A run that flagged anomalies is ruled on through each of them. */
        anomalies: ReviewDecisionSchema.array(),
      }),
    },
    update: {
      body: z.object({
        started_processing_at: NullishDateTimeWithOffset,
        finished_processing_at: NullishDateTimeWithOffset,
        removed_payload_at: NullishDateTimeWithOffset,
      }),
    },
  };

  constructor(private api: Auth) {}

  /** The slim, cross-project list — see `schemas.listSlim`. */
  public async listSlim(
    query?: z.input<typeof CyclesEndpoint.schemas.listSlim.parameters>,
  ): ActionReturnType<z.infer<typeof CyclesEndpoint.schemas.listSlim.response>> {
    const params = validateInput(CyclesEndpoint.schemas.listSlim.parameters, query ?? {});
    if (!params.ok) return params;
    return request({
      log: ["Cycles", "listSlim"],
      method: "get",
      path: [CyclesEndpoint.basePath, "list"],
      query: params.data,
      response: CyclesEndpoint.schemas.listSlim.response,
    });
  }

  /**
   * The API worker's run log (see {@link ApiRunLogSchema}). A cycle that died before its first
   * request has no file and answers 404, so errors stay with the caller rather than a toast.
   */
  public apiRunLog(id: string | number): ActionReturnType<ApiRunLog> {
    return request({
      log: ["Cycles", "apiRunLog"],
      method: "get",
      path: [CyclesEndpoint.basePath, id, "log-file"],
      stripTrailingSlash: true,
      responseAs: "text",
      localErrorHandling: true,
      response: ApiRunLogSchema,
    });
  }

  public read(id: string | number): ActionReturnType<z.infer<typeof CyclesEndpoint.schemas.read.response>> {
    return request({
      log: ["Cycles", "read"],
      method: "get",
      path: [CyclesEndpoint.basePath, id],
      response: CyclesEndpoint.schemas.read.response,
    });
  }

  public async update(
    id: string,
    body: z.infer<typeof CyclesEndpoint.schemas.update.body>,
  ): ActionReturnType<undefined> {
    const bodyValidated = validateInput(CyclesEndpoint.schemas.update.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Cycles", "update"],
      method: "put",
      path: [CyclesEndpoint.basePath, id],
      json: bodyValidated.data,
    });
  }

  public delete(id: string): ActionReturnType<undefined> {
    return request({
      log: ["Cycles", "delete"],
      method: "delete",
      path: [CyclesEndpoint.basePath, id],
    });
  }

  public cancel(id: string | number): ActionReturnType<undefined> {
    return request({
      log: ["Cycles", "cancel"],
      method: "post",
      path: [CyclesEndpoint.basePath, id, "cancel"],
      stripTrailingSlash: true,
    });
  }

  public review(id: string | number): ActionReturnType<CycleReview> {
    return request({
      log: ["Cycles", "review"],
      method: "get",
      path: [CyclesEndpoint.basePath, id, "review"],
      stripTrailingSlash: true,
      response: CyclesEndpoint.schemas.review.response,
    });
  }

  /** Without `anomaly_ids` the verdict covers every anomaly — or the run itself when it flagged none. */
  public async reviewDecide(
    id: string | number,
    verdict: CycleReviewVerdict | "revoke",
    body: z.infer<typeof CyclesEndpoint.schemas.review.body> = {},
  ): ActionReturnType<CycleReview> {
    const bodyValidated = validateInput(CyclesEndpoint.schemas.review.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Cycles", "reviewDecide"],
      method: "post",
      path: [CyclesEndpoint.basePath, id, "review", verdict],
      stripTrailingSlash: true,
      json: bodyValidated.data,
      response: CyclesEndpoint.schemas.review.response,
    });
  }
}

export { CyclesEndpoint };

/* 
all states a CYCLE can be in:
- Success - step.type === 'end_task' step.expected_result_success === true 
- Failed - step.type === 'end_task' && (step.expected_result_success === false|null || step.error !== null)
- Skipped - step.type === 'end_task' && step.is_path_saved === false
- Anomaly detected - step.phase === 'run' && step.type === 'end_task' && step.anomalies_detected === true
- Running - !!step.phase && step.type === 'beg_task' - used to signify learn/relearn too, above steps are only relevant when step.phase === 'run
*/
